import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Sparkles, X, Send, Copy, Loader2, Brain } from "lucide-react";
import { toast } from "sonner";

const PROVIDERS = [
  { id: "anthropic", label: "Claude 4.5", color: "#D97757" },
  { id: "openai",    label: "GPT-5",      color: "#10A37F" },
  { id: "gemini",    label: "Gemini 3",   color: "#4285F4" },
];

const PATH_CONTEXT = {
  "/studio/flyer":    { ctx: "flyer",    label: "Flyer",     suggestions: ["Aide-moi à écrire un prompt pour mon restaurant", "Comment intégrer mon logo dans la pub ?", "Quel style choisir pour un salon de coiffure ?"] },
  "/studio/video":    { ctx: "video",    label: "Vidéo",     suggestions: ["Écris-moi un scénario de pub vidéo 8s", "Quel format pour Instagram Reels ?", "Décris une scène cinématique pour un produit de luxe"] },
  "/studio/sequence": { ctx: "sequence", label: "Multi-scènes", suggestions: ["Découpe-moi une pub en 3 scènes", "Quelles transitions pour une pub dynamique ?"] },
  "/studio/voice":    { ctx: "voice",    label: "Voix off",  suggestions: ["Rédige un script voix off 20 secondes", "Choisis le ton pour une pub mode", "Adapte ce texte en version cinéma"] },
  "/studio/composer": { ctx: "compose",  label: "Assembleur", suggestions: ["Quelle musique pour une pub corporate ?", "Comment doser le volume musique vs voix ?"] },
};

function ChatBubble({ role, content, onUsePrompt }) {
  // Extract [PROMPT]...[/PROMPT] blocks
  const parts = [];
  const regex = /\[PROMPT\]([\s\S]*?)\[\/PROMPT\]/g;
  let m, last = 0, key = 0;
  while ((m = regex.exec(content)) !== null) {
    if (m.index > last) parts.push({ type: "text", text: content.slice(last, m.index), k: key++ });
    parts.push({ type: "prompt", text: m[1].trim(), k: key++ });
    last = m.index + m[0].length;
  }
  if (last < content.length) parts.push({ type: "text", text: content.slice(last), k: key++ });

  return (
    <div className={`flex ${role === "user" ? "justify-end" : "justify-start"} mb-3`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${role === "user" ? "bg-gold text-black" : "bg-white/5 text-white border border-white/10"}`}>
        {parts.length === 0 ? (
          <span className="whitespace-pre-wrap leading-relaxed">{content}</span>
        ) : parts.map(p => p.type === "text" ? (
          <span key={p.k} className="whitespace-pre-wrap leading-relaxed">{p.text}</span>
        ) : (
          <div key={p.k} className="my-2 p-3 rounded-lg bg-obsidian border border-gold/40 relative">
            <pre className="whitespace-pre-wrap text-xs text-gold/90 font-mono">{p.text}</pre>
            <button onClick={() => onUsePrompt(p.text)} className="absolute top-2 right-2 text-[10px] uppercase tracking-widest text-gold/80 hover:text-gold flex items-center gap-1">
              <Copy className="w-3 h-3" /> Copier
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AssistantWidget() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [provider, setProvider] = useState("anthropic");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const ctx = PATH_CONTEXT[pathname] || null;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  if (!user) return null; // only logged-in users

  const send = async (msgOverride) => {
    const msg = (msgOverride ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    const newMsgs = [...messages, { id: `u_${Date.now()}`, role: "user", content: msg }];
    setMessages(newMsgs);
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/chat", {
        message: msg,
        history: messages,
        provider,
        context: ctx?.ctx,
        session_id: sessionId,
      });
      setSessionId(data.session_id);
      setMessages([...newMsgs, { id: `a_${Date.now()}`, role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur assistant");
      setMessages([...newMsgs, { id: `a_${Date.now()}`, role: "assistant", content: "Désolé, j'ai rencontré un souci. Réessayez." }]);
    } finally { setLoading(false); }
  };

  const copyPrompt = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Prompt copié — collez-le dans le studio"));
  };

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button data-testid="assistant-open" onClick={() => setOpen(true)}
          className="fixed bottom-6 left-6 z-[80] gold-btn !py-3 !px-5 shadow-2xl animate-gold-pulse">
          <Sparkles className="w-4 h-4" />
          <span className="hidden sm:inline">Conseiller IA</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div data-testid="assistant-panel" className="fixed inset-y-0 right-0 z-[80] w-full sm:w-[440px] flex flex-col"
          style={{ background: "rgba(10,10,10,0.96)", backdropFilter: "blur(16px)", borderLeft: "1px solid rgba(212,175,55,0.25)" }}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-gold" />
              <div>
                <div className="font-heading text-lg">Conseiller pub IA</div>
                <div className="text-[10px] uppercase tracking-widest text-white/40">{ctx ? `Contexte: ${ctx.label}` : "Studio général"}</div>
              </div>
            </div>
            <button data-testid="assistant-close" onClick={() => setOpen(false)} className="p-2 rounded-lg hover:bg-white/5">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Provider selector */}
          <div className="px-4 py-3 border-b border-white/5 flex gap-2">
            {PROVIDERS.map(p => (
              <button key={p.id} data-testid={`provider-${p.id}`} onClick={() => setProvider(p.id)}
                className={`flex-1 px-2 py-1.5 rounded-full text-[11px] uppercase tracking-widest font-medium transition ${provider===p.id ? "text-black" : "text-white/60 hover:text-white border border-white/10"}`}
                style={provider===p.id ? { background: p.color } : {}}>
                {p.label}
              </button>
            ))}
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 scroll-thin">
            {messages.length === 0 ? (
              <div className="text-center py-10">
                <Sparkles className="w-8 h-8 text-gold mx-auto" />
                <div className="mt-3 font-heading text-lg">Comment puis-je vous aider ?</div>
                <p className="text-white/50 text-sm mt-1 max-w-xs mx-auto">Je rédige vos prompts, scénarios et scripts voix off. Demandez-moi !</p>
                {ctx && (
                  <div className="mt-5 space-y-2">
                    {ctx.suggestions.map((s, i) => (
                      <button key={s} data-testid={`suggestion-${i}`} onClick={() => send(s)}
                        className="block w-full text-left px-3 py-2 rounded-lg bg-white/5 hover:bg-gold/10 hover:text-gold border border-white/10 text-xs text-white/70 transition">
                        💡 {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              messages.map((m) => <ChatBubble key={m.id} role={m.role} content={m.content} onUsePrompt={copyPrompt} />)
            )}
            {loading && (
              <div className="flex items-center gap-2 text-sm text-white/50 px-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Réflexion...
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-4 border-t border-white/10">
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
              <input
                data-testid="assistant-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Posez votre question..."
                className="flex-1 bg-obsidian-light border border-white/10 rounded-full px-4 py-2.5 text-sm outline-none focus:border-gold/50"
              />
              <button data-testid="assistant-send" type="submit" disabled={loading || !input.trim()}
                className="gold-btn !py-2 !px-3 disabled:opacity-50">
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
